import axios from 'axios';
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../store';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:3000';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 300000, // 5 minutes — allows large video uploads
  headers: { 'Content-Type': 'application/json' },
});

/* ---------- Token refresh helper ---------- */
let refreshPromise: Promise<boolean> | null = null;

/**
 * Silently refresh the access token using the stored refresh token.
 * De-duplicated: concurrent calls share a single in-flight request.
 * Returns true if refresh succeeded, false otherwise.
 */
async function refreshAccessToken(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const { refreshToken, clearAuth } = useAuthStore.getState();
    if (!refreshToken) {
      clearAuth();
      return false;
    }
    try {
      const res = await axios.post(`${API_BASE_URL}/auth/refresh`, { refreshToken });
      const payload = res.data.data ?? res.data;
      useAuthStore.getState().setAuth(payload.user, payload.accessToken, payload.refreshToken);
      return true;
    } catch {
      clearAuth();
      return false;
    }
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

/**
 * Check if the stored JWT is expired (or about to expire within 60s).
 */
function isTokenExpiringSoon(): boolean {
  const { accessToken } = useAuthStore.getState();
  if (!accessToken) return true;
  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1]));
    const expiresAt = payload.exp * 1000;
    return Date.now() > expiresAt - 60_000; // 60s buffer
  } catch {
    return true;
  }
}

/* ---------- Request interceptor ---------- */
api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  // Proactively refresh token if it's expired or expiring within 60s.
  // This prevents 401s mid-upload (FormData can't be re-sent on retry).
  if (isTokenExpiringSoon() && useAuthStore.getState().refreshToken) {
    await refreshAccessToken();
  }

  const { accessToken } = useAuthStore.getState();
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  // Tenant ID from localStorage (set at login)
  const tenantId = localStorage.getItem('vqs_tenant_id');
  if (tenantId) {
    config.headers['X-Tenant-ID'] = tenantId;
  }

  return config;
});

/* ---------- Response interceptor ---------- */
api.interceptors.response.use(
  (response) => {
    if (response.data && 'data' in response.data) {
      return { ...response, data: response.data };
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // 401 Unauthorized: attempt token refresh once, then retry
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Don't retry multipart/file uploads — body stream is consumed
      const isFileUpload = originalRequest.data instanceof FormData;

      const refreshed = await refreshAccessToken();

      if (refreshed && !isFileUpload) {
        const { accessToken } = useAuthStore.getState();
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      }
      // If file upload: refresh succeeded but we can't re-send the file.
      // The caller will get the 401 error — but next request will use the fresh token.
    }

    return Promise.reject(error);
  },
);

/**
 * Run once on app boot.
 * If the stored access token is already expired, attempt a silent refresh.
 * If refresh fails, clear stale auth so the UI doesn't show "Go to Dashboard"
 * for a session that can no longer be resumed.
 */
export async function initAuthSession(): Promise<void> {
  const { accessToken, refreshToken } = useAuthStore.getState();
  if (!accessToken || !refreshToken) return;
  if (isTokenExpiringSoon()) {
    await refreshAccessToken();
  }
}

export default api;
